const normalizeYouTubeUrl = source => {
    try {
        const url = new URL(source);
        const isYouTubeHost = /(^|\.)youtube\.com$/i.test(url.hostname);
        const shortsMatch = url.pathname.match(/^\/shorts\/([^/]+)\/?$/);

        if (!isYouTubeHost || !shortsMatch) return source;

        url.pathname = '/watch';
        url.searchParams.set('v', shortsMatch[1]);

        return url.toString();
    } catch (error) {
        return source;
    }
};

class Video {
    constructor(el) {
        if (!this.set(el)) return;

        if (
            document.readyState === 'complete' ||
            (document.readyState !== 'loading' &&
                !document.documentElement.doScroll)
        ) {
            this.init();
        } else {
            document.addEventListener('DOMContentLoaded', this.init.bind(this));
        }
    }

    set(el) {
        if (!el) return false;

        this.root = el;
        this.videoEl = this.root.querySelector('[data-video-video]');
        this.toggleEl = this.root.querySelector('[data-video-toggle]');
        this.toggleIconEl = this.root.querySelector('[data-video-toggle] i');
        this.wrapperEl = this.root.querySelector('[data-video-wrapper]');

        this.player = null;
        this.playerPromise = null;

        return true;
    }

    init() {
        this.setToggle();

        // Prepare the player before the first click. Creating the YouTube
        // iframe inside the click handler makes the eventual play() call run
        // after the browser has already lost the user activation.
        this.initPlayer().catch(error => {
            console.error('Video player could not be initialized.', error);
        });
    }

    async initPlayer() {
        if (!this.videoEl || this.player) return this.player;
        if (this.playerPromise) return this.playerPromise;

        this.playerPromise = this.createPlayer();

        try {
            return await this.playerPromise;
        } finally {
            this.playerPromise = null;
        }
    }

    async createPlayer() {
        const [{ default: videojs }] = await Promise.all([
            import('video.js'),
            import('videojs-youtube'),
        ]);

        const options = {
            controls: false,
            // The player is prepared before interaction so the first click can
            // start playback immediately.
            preload: 'auto',
            fluid: false,
        };

        if (this.videoEl.dataset.videoType === 'youtube') {
            options.techOrder = ['youtube'];
            options.youtube = {
                customVars: {
                    origin: window.location.origin,
                },
                playsinline: 1,
            };
            options.sources = [
                {
                    type: 'video/youtube',
                    src: normalizeYouTubeUrl(this.videoEl.dataset.videoSrc),
                },
            ];
        } else {
            options.techOrder = ['html5'];
            options.sources = [
                {
                    type: this.videoEl.dataset.videoMimeType || 'video/mp4',
                    src: this.videoEl.dataset.videoSrc,
                },
            ];
        }

        this.player = videojs(this.videoEl, options);

        this.player.on('play', () => this.setPlayingState(true));
        this.player.on('pause', () => this.setPlayingState(false));
        this.player.on('ended', () => this.setPlayingState(false));

        return this.player;
    }

    setToggle() {
        if (!this.toggleEl) return;

        this.onToggle = this.onToggleClick.bind(this);
        this.toggleEl.addEventListener('click', this.onToggle);
    }

    async onToggleClick() {
        try {
            const player = await this.initPlayer();
            if (!player) return;

            if (player.paused()) {
                await player.play();
            } else {
                player.pause();
            }
        } catch (error) {
            console.error('Video player could not be initialized.', error);
        }
    }

    setPlayingState(isPlaying) {
        if (isPlaying) {
            this.toggleIconEl.classList.remove('icon-play');
            this.toggleIconEl.classList.add('icon-pause');
            this.wrapperEl.classList.add('is-playing');
        } else {
            this.toggleIconEl.classList.remove('icon-pause');
            this.toggleIconEl.classList.add('icon-play');
            this.wrapperEl.classList.remove('is-playing');
        }
    }
}

const initVideo = () => {
    const sections = document.querySelectorAll('[data-video]');
    if (sections.length) {
        sections.forEach(item => new Video(item));
    }
};

initVideo();
